import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Building2, Mic2, Save } from "lucide-react";
import { toast } from "sonner";

export default function ManagerMapping() {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null); // manager being edited
  const [labelSel, setLabelSel] = useState([]);
  const [artistSel, setArtistSel] = useState([]);

  const load = () => api.get("/manager-mapping-data").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const openEdit = (m) => {
    setEdit(m);
    setLabelSel(m.managed_label_ids || []);
    setArtistSel(m.managed_artist_ids || []);
  };
  const toggle = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const save = async () => {
    await api.post(`/users/${edit.id}/managed-labels`, { ids: labelSel });
    await api.post(`/users/${edit.id}/managed-artists`, { ids: artistSel });
    toast.success(`Mapping updated for ${edit.name}`);
    setEdit(null);
    load();
  };

  if (!data) return <p className="text-muted-foreground">Loading…</p>;
  const labelName = (id) => data.labels.find((l) => l.id === id)?.name || id;
  const artistName = (id) => data.solo_artists.find((a) => a.id === id)?.name || id;

  return (
    <div className="space-y-6" data-testid="manager-mapping-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Manager Mapping</h1>
        <p className="mt-1 text-muted-foreground">Assign labels and solo artists to each Label Manager.</p>
      </div>

      <Card className="overflow-hidden">
        <div className="flex items-center gap-2 border-b border-border p-4"><Users className="h-5 w-5 text-primary" /><h3 className="font-heading text-lg font-semibold">Label Managers ({data.managers.length})</h3></div>
        <div className="divide-y divide-border">
          {data.managers.length === 0 && <p className="p-6 text-center text-muted-foreground">No label managers found.</p>}
          {data.managers.map((m) => (
            <div key={m.id} className="flex flex-wrap items-start justify-between gap-4 p-4" data-testid={`manager-row-${m.id}`}>
              <div className="min-w-[200px]">
                <p className="font-semibold">{m.name}</p>
                <p className="text-xs text-muted-foreground">{m.email}</p>
              </div>
              <div className="flex flex-1 flex-wrap gap-1.5">
                {(m.managed_label_ids || []).map((id) => <Badge key={id} variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400"><Building2 className="mr-1 h-3 w-3" />{labelName(id)}</Badge>)}
                {(m.managed_artist_ids || []).map((id) => <Badge key={id} variant="outline" className="border-primary/30 bg-primary/10 text-primary"><Mic2 className="mr-1 h-3 w-3" />{artistName(id)}</Badge>)}
                {(m.managed_label_ids || []).length === 0 && (m.managed_artist_ids || []).length === 0 && <span className="text-sm text-muted-foreground">Nothing mapped yet</span>}
              </div>
              <Button size="sm" onClick={() => openEdit(m)} data-testid={`edit-mapping-${m.id}`}>Edit Mapping</Button>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Map for {edit?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-semibold">Labels</p>
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
                {data.labels.length === 0 && <p className="text-sm text-muted-foreground">No labels.</p>}
                {data.labels.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent">
                    <Checkbox checked={labelSel.includes(l.id)} onCheckedChange={() => toggle(labelSel, setLabelSel, l.id)} data-testid={`map-label-${l.id}`} />{l.name}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-sm font-semibold">Solo Artists</p>
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
                {data.solo_artists.length === 0 && <p className="text-sm text-muted-foreground">No solo artists.</p>}
                {data.solo_artists.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent">
                    <Checkbox checked={artistSel.includes(a.id)} onCheckedChange={() => toggle(artistSel, setArtistSel, a.id)} data-testid={`map-artist-${a.id}`} />{a.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} data-testid="save-mapping-btn"><Save className="mr-2 h-4 w-4" />Save Mapping</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
